<template>
  <span :class="classes">
    <slot />
  </span>
</template>

<script setup lang="ts">
  /**
   * UiBadge — Tailwind primitive replacing BootstrapVue-Next's `b-badge`.
   *
   * Mirrors BVN's API: `variant` controls the colour and `pill` swaps the
   * default rounded shape for fully rounded corners.
   *
   * Variants use Tailwind's default colour scale (blue/green/yellow/red/etc.)
   * because the project's tailwind.config.js semantic tokens are flat
   * single-shade colours that don't pair well with white text on solid badges.
   */
  import { computed } from 'vue'

  defineOptions({ name: 'UiBadge' })

  interface Props {
    variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark'
    pill?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    variant: 'secondary',
    pill: false,
  })

  const VARIANTS: Record<NonNullable<Props['variant']>, string> = {
    primary:   'tw-bg-blue-600 tw-text-white',
    secondary: 'tw-bg-gray-500 tw-text-white',
    success:   'tw-bg-green-600 tw-text-white',
    danger:    'tw-bg-red-600 tw-text-white',
    warning:   'tw-bg-yellow-400 tw-text-gray-900',
    info:      'tw-bg-cyan-500 tw-text-white',
    light:     'tw-bg-gray-100 tw-text-gray-900',
    dark:      'tw-bg-gray-800 tw-text-white',
  }

  const BASE = 'tw-inline-block tw-px-2 tw-py-0.5 tw-text-xs tw-font-bold tw-leading-none tw-text-center tw-align-baseline tw-whitespace-nowrap'

  const classes = computed(() => [
    BASE,
    VARIANTS[props.variant],
    props.pill ? 'tw-rounded-full' : 'tw-rounded',
  ])
</script>
